import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { watch } from 'vue'

import semver from 'semver'
import { addWeeks, isAfter } from 'date-fns'
import Vuetify from '@/plugins/vuetify'
import Preferences from '@/plugins/preferences'
import bus from '@/plugins/eventbus'
import { stateGetters, stateSetters, watchGetterAndPersist } from '@/utils/store/store-utils'
import { getDeviceColorSchemePreference } from '@/utils/helpers'
import { loadAsyncLocale } from '@/plugins/i18n'

const defaultState = {
  id: null,
  platform: null,
  os: null,
  browser: null,
  browserVersion: null,
  breakpoint: null,
  language: null,
  theme: 'auto',
  online: true,
  localeLoaded: false,
  appVersion: APPVERSION,
  latestAppVersion: APPVERSION,
  appVersionUpdatedAt: new Date(),
  forceUpdateLayout: false,
}

export default {
  name: 'device',
  namespaced: true,
  state: () => defaultState,
  getters: {
    ...stateGetters(defaultState),
    all(s) {
      return {
        id: s.id,
        platform: s.platform,
        os: s.os,
        browser: s.browser,
        browserVersion: s.browserVersion,
        language: s.language,
      }
    },
    showUpdateAppOverlay(s) {
      return semver.lt(s.appVersion, s.latestAppVersion) || s.forceUpdateLayout
    },
    showWhatIsNew(s) {
      const twoWeeksAfterRelease = addWeeks(s.appVersionUpdatedAt, 2)
      return !isAfter(new Date(), twoWeeksAfterRelease)
    },
    isMobile(s, g) {
      return !g.isDesktop
    },
    isDesktop(s, g) {
      // return false // DEBUG
      if (!g.breakpoint) return true // HACK race condition with watcher below
      if (g.isNative && g.breakpoint?.smAndDown) return false

      return g.breakpoint?.mdAndUp || g.isExtensionPopup
    },
    isNative() {
      // return true // DEBUG
      return Capacitor.isNativePlatform()
    },
    isWeb(s, g) {
      // return true // DEBUG
      if (g.isExtension) return false
      return Capacitor.getPlatform() === 'web'
    },
    isExtension() {
      // return true // DEBUG
      return BUILD_TARGET === 'extension'
    },
    isExtensionPopup() {
      //return true // DEBUG
      return document.body.classList.contains('rubra-extension-popup')
    },
    isExtensionMini(s, g) {
      //return true // DEBUG
      return g.isExtensionPopup && EXT_MINI
    },
    isExtensionPopupMini() {
      return window.innerWidth < 500 && BUILD_TARGET === 'extension'
    },
  },
  mutations: {
    ...stateSetters(defaultState),
  },
  actions: {
    setTheme({ commit }, theme) {
      let isDark = theme === 'dark'

      if (theme === 'auto') {
        isDark = getDeviceColorSchemePreference() === 'dark'
      }

      // In Vuetify 3, theme is set directly on the theme object, not via framework.theme
      Vuetify.theme.global.name.value = isDark ? 'dark' : 'light'
      commit('setTheme', theme)

      // for initial page load, write to sync storage
      localStorage.setItem('lastTheme', theme)
    },
    onOsThemeSwitch({ getters }, isDark) {
      const { theme } = getters
      if (theme !== 'auto') return

      // In Vuetify 3, theme is set directly on the theme object, not via framework.theme
      Vuetify.theme.global.name.value = isDark ? 'dark' : 'light'
    },
    async loadLocale({ commit, getters }, locale) {
      await loadAsyncLocale(locale)

      if (!getters.localeLoaded) {
        commit('setLocaleLoaded', true)
      }
    },
    async checkDeviceConnectivity({ commit }) {
      const health = await this.$api.app.getHealth()
      commit('setOnline', !!health)
    },
    async checkAppVersion({ commit }) {
      const { minRequiredAppVersionUpdatedAt, minRequiredAppVersion } = await this.$api.version.fetchAppVersion()
      commit('setLatestAppVersion', minRequiredAppVersion)
      commit('setAppVersionUpdatedAt', new Date(minRequiredAppVersionUpdatedAt))
    },
    // New action to update breakpoint from components
    updateBreakpoint({ commit }, breakpointData) {
      commit('setBreakpoint', breakpointData)
    },
  },
}

bus.$once('app:created', async app => {
  app.config.globalProperties.$store.dispatch('device/checkAppVersion').catch(console.error)

  // store device id in preferences for syncing with extension
  const deviceId = (await Preferences.get('deviceId')) ?? (await Device.getId()).identifier
  watchGetterAndPersist(app.config.globalProperties.$store, 'device/id', 'deviceId')
  app.config.globalProperties.$store.commit('device/setId', deviceId)

  const info = await Device.getInfo()

  if (BUILD_TARGET === 'extension') {
    info.platform = 'extension'
  }

  app.config.globalProperties.$store.commit('device/setPlatform', info.platform)
  app.config.globalProperties.$store.commit('device/setOs', info.operatingSystem)

  // if in web platform, get browser as well
  if (!Capacitor.isNativePlatform()) {
    const { default: Bowser } = await import('bowser')
    const browser = Bowser.getParser(window.navigator.userAgent).getBrowser()

    app.config.globalProperties.$store.commit('device/setBrowser', browser.name)
    app.config.globalProperties.$store.commit('device/setBrowserVersion', browser.version)
  }

  // Breakpoint handling has been moved to DisplayWatcher component
  // which will use the useDisplay composable and dispatch updateBreakpoint action

  // theme settings
  const theme = (await Preferences.get('theme')) ?? 'auto'
  app.config.globalProperties.$store.dispatch('device/setTheme', theme)
  watchGetterAndPersist(app.config.globalProperties.$store, 'device/theme', 'theme')

  // OS theme sync
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', ({ matches: isDark }) => {
      app.config.globalProperties.$store.dispatch('device/onOsThemeSwitch', isDark)
    })

  // language sync
  if (app.config.globalProperties.$i18n) {
    const i18n = app.config.globalProperties.$i18n
    const getLocale = () => {
      if (!i18n) return null

      // Handle different possible structures of i18n locale in Vue 3
      if (i18n.locale && typeof i18n.locale === 'object' && 'value' in i18n.locale) {
        return i18n.locale.value // Composition API ref
      } if (i18n.locale) {
        return i18n.locale // Direct property
      } if (typeof i18n.getLocaleMessage === 'function') {
        // Try to get locale from global property if available
        return i18n.getLocaleMessage()
      }

      return null
    }

    // Handle null returns from getLocale
    const currentLocale = getLocale()
    if (currentLocale) {
      watch(getLocale, lang => {
        if (lang) {
          app.config.globalProperties.$store.commit('device/setLanguage', lang)
        }
      }, { immediate: true })
    } else {
      console.warn('Unable to detect i18n locale structure')
    }
  } else {
    console.warn('i18n not found on global properties')
  }

  watchGetterAndPersist(app.config.globalProperties.$store, 'device/language', 'lang')
})
